import React, { FC, useEffect } from 'react';
import { NumberInput, SelectInput, useTranslate } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useField, useForm } from 'react-final-form';
import lodashGet from 'lodash/get';

import { useHandbook } from '~/hooks';
import { SelectInput as MuiSelectInput } from '~/components';
import { CurrencySymbols, NumberEx } from '~/utils';
import { Amount } from '~/types';
import { isEmpty } from 'lodash';

const useStyles = makeStyles({
  selectInput: {
    minWidth: 100,
  },
});

const validateAmountValue = (source = 'amount', nonNullable: boolean, required: boolean) => (
  _: any,
  values: any
) => {
  const amount: Amount = lodashGet(values, source);
  if (amount) {
    const { value, currency } = amount;
    if ((currency && !value?.toString()) || (required && !value?.toString()))
      return {
        message: 'ra.validation.required',
        isRequired: true,
      };
    if (nonNullable && value.toString() === '0')
      return {
        message: 'components.ra.inputs.AmountInput.nonNullable',
      };
  }
};

const validateAmountCurrency = (source = 'amount', required: boolean) => (_: any, values: any) => {
  const amount: Amount = lodashGet(values, source);
  if (amount) {
    const { value, currency } = amount;
    if ((value?.toString() && !currency) || (required && !currency))
      return {
        message: 'ra.validation.required',
        // in some reason it's field does not affect on "required={true}" SelectInput prop
        isRequired: true,
      };
  }
};
export interface Props {
  source?: string;
  amountLabel?: string;
  currencyLabel?: string;
  solutionId?: string;
  permanentSelectedCurrency?: string;
  currencyDisabled?: boolean;
  positiveOnly?: boolean;
  nonNullable?: boolean;
  required?: boolean;
  initialValue?: number;
  currencies?: { id: string; name: string }[];
  shouldClearOnUnmount?: boolean;
  allowEmpty?: boolean;
  maxNumber?: number;
  [k: string]: any;
}

const AmountInput = ({
  source = 'amount',
  amountLabel,
  currencyLabel,
  solutionId,
  permanentSelectedCurrency,
  currencyDisabled,
  positiveOnly = false,
  nonNullable = false,
  required = false,
  initialValue,
  currencies: currenciesFromProps,
  shouldClearOnUnmount,
  allowEmpty = false,
  maxNumber,
  ...rest
}: Props) => {
  const classes = useStyles();

  const currencySymbols: { [key: string]: string } = CurrencySymbols;

  const translate = useTranslate();
  const t = (key: string): string => translate(`components.ra.inputs.AmountInput.${key}`);

  const { data } = useHandbook(solutionId);

  let currencies = [];
  if (currenciesFromProps) {
    currencies = currenciesFromProps;
  } else if (data && !permanentSelectedCurrency) {
    currencies = (data.currencies || []).map((c) => {
      return { id: c.id, name: currencySymbols[c.id] || c.id };
    });
  } else if (permanentSelectedCurrency) {
    currencies = [
      {
        id: permanentSelectedCurrency,
        name: currencySymbols[permanentSelectedCurrency] || permanentSelectedCurrency,
      },
    ];
  } else {
    currencies = Object.keys(CurrencySymbols).map((key) => {
      return { id: key, name: currencySymbols[key] };
    });
  }

  const valueField = useField(`${source}.value`);
  const currencyField = useField(`${source}.currency`);

  return (
    <Grid container spacing={1}>
      <Grid item xs={8}>
        <NumberInput
          {...rest}
          label={amountLabel || t(`amount`)}
          source={`${source}.value`}
          fullWidth
          parse={positiveOnly ? NumberEx.positive : undefined}
          min={positiveOnly ? 0 : undefined}
          max={maxNumber}
          // Adds custom * depending on currency.
          required={
            required === false && !!permanentSelectedCurrency
              ? false
              : currencyField.input.value !== '' || required
          }
          // Custom validation which doesn't add * but makes input required and adds validation message.
          validate={validateAmountValue(source, nonNullable, required)}
          initialValue={initialValue}
        />
      </Grid>
      <Grid item xs={4}>
        {typeof valueField.input.value === 'number' || !permanentSelectedCurrency || required ? (
          <CurrencySelect
            {...rest}
            className={classes.selectInput}
            label={currencyLabel || t(`currency`)}
            source={`${source}.currency`}
            choices={
              isEmpty(currencies)
                ? [{ id: undefined, name: translate('utils.noHandbookValues') }]
                : currencies
            }
            // Custom validation which doesn't add * but makes input required and adds validation message.
            validate={validateAmountCurrency(source, required)}
            // Adds custom * depending on amount.
            required={
              required === false && !!permanentSelectedCurrency
                ? false
                : valueField.input.value !== '' || required
            }
            defaultValue={permanentSelectedCurrency}
            disabled={currencyDisabled || permanentSelectedCurrency}
            fullWidth
            shouldClearOnUnmount={shouldClearOnUnmount}
            allowEmpty={allowEmpty}
          />
        ) : (
          <MuiSelectInput
            value={permanentSelectedCurrency}
            label={currencyLabel || t(`currency`)}
            options={[
              {
                value: permanentSelectedCurrency,
                text: currencySymbols[permanentSelectedCurrency] || permanentSelectedCurrency,
              },
            ]}
            disabled
          />
        )}
      </Grid>
    </Grid>
  );
};

const CurrencySelect: FC<any> = (props) => {
  const { source, shouldClearOnUnmount = true } = props; // Add a prop to control clearing behavior.
  const form = useForm();

  useEffect(() => {
    return () => {
      if (shouldClearOnUnmount) {
        form.change(source, undefined); // Clear the field only if the condition is met.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClearOnUnmount]);

  return <SelectInput {...props} />;
};

export default AmountInput;
